<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import LikeBurstFeedback from './LikeBurstFeedback.vue'
import ViewerIcon from './ViewerIcon.vue'

const props = defineProps({
  liked: Boolean,
  likes: {
    type: Number,
    default: 0
  },
  cameraMode: {
    type: String,
    default: 'orbit'
  },
  cameraTrajectoryAvailable: Boolean,
  cameraTrajectoryPlaying: Boolean,
  videoRecordAvailable: Boolean,
  videoRecording: Boolean,
  imageVideoGenerating: Boolean,
  imageVideoStatus: { type: String, default: 'idle' },
  landscapeMode: Boolean,
  aiLoading: Boolean,
  aiStatus: {
    type: String,
    default: 'idle'
  },
  aiNoticeVisible: Boolean,
  noticeActive: Boolean,
  gestureNoticeActive: Boolean,
  annotationEditing: Boolean,
  annotationEditable: Boolean,
  collapsed: Boolean,
  tourExpanded: Boolean,
  arAvailable: Boolean
})

const emit = defineEmits(['action', 'update:collapsed'])

const effectiveCollapsed = computed(() => props.collapsed && !props.tourExpanded)
const settingsOpen = ref(false)
const aiToolsOpen = ref(false)
const aiButtonLabel = computed(() => {
  if (props.aiLoading || props.aiStatus === 'waiting') return 'AI 导游正在查询资料'
  if (props.aiStatus === 'success') return '查看 AI 导游'
  if (props.aiStatus === 'error') return 'AI 导游失败，点击查看'
  return 'AI 导游'
})
const aiGuideState = computed(() => {
  if (props.aiLoading || props.aiStatus === 'waiting') return 'working'
  if (props.aiStatus === 'success') return 'ready'
  if (props.aiStatus === 'error') return 'error'
  return 'idle'
})
const aiGuideStatusLabel = computed(() => ({
  idle: '未生成',
  working: '生成中',
  ready: '已生成',
  error: '生成失败'
})[aiGuideState.value])
const imageVideoState = computed(() => {
  if (props.imageVideoStatus === 'ready') return 'ready'
  if (props.imageVideoStatus === 'error') return 'error'
  if (props.imageVideoStatus === 'idle') return 'idle'
  return 'working'
})
const imageVideoStatusLabel = computed(() => ({
  idle: '未生成',
  working: '生成中',
  ready: '已生成',
  error: '生成失败'
})[imageVideoState.value])
const imageVideoButtonLabel = computed(() => {
  const action = imageVideoState.value === 'ready'
    ? '播放 AI 视频'
    : imageVideoState.value === 'error'
      ? '重试 AI 视频任务'
      : imageVideoState.value === 'working'
        ? '查看 AI 视频进度'
        : '生成 AI 视频'
  return `${action}，状态：${imageVideoStatusLabel.value}`
})
const hintsVisible = ref(false)
const longHintsVisible = ref(false)
let hintTimer = 0
let hintPending = false
let hintCanOverlapNotice = false
let initialHintsShown = false

function revealButtonHints() {
  window.clearTimeout(hintTimer)
  hintsVisible.value = false
  if (!hintPending || props.tourExpanded || (props.noticeActive && !hintCanOverlapNotice)) return
  nextTick(() => {
    if (!hintPending || props.tourExpanded || (props.noticeActive && !hintCanOverlapNotice)) return
    hintsVisible.value = true
    hintTimer = window.setTimeout(() => {
      hintsVisible.value = false
      longHintsVisible.value = false
      hintPending = false
      hintCanOverlapNotice = false
    }, longHintsVisible.value ? 4200 : 2400)
  })
}

function cancelButtonHints() {
  window.clearTimeout(hintTimer)
  hintPending = false
  hintCanOverlapNotice = false
  hintsVisible.value = false
  longHintsVisible.value = false
}

function showInitialHintsWithGestureNotice(active) {
  if (initialHintsShown || !active || props.landscapeMode) return
  initialHintsShown = true
  hintPending = true
  hintCanOverlapNotice = true
  longHintsVisible.value = true
  revealButtonHints()
}

function toggleSettings() {
  aiToolsOpen.value = false
  settingsOpen.value = !settingsOpen.value
}

function toggleAiTools() {
  settingsOpen.value = false
  aiToolsOpen.value = !aiToolsOpen.value
}

function handleSettingsAction(action) {
  settingsOpen.value = false
  emit('action', action)
}

function handleAiToolAction(action) {
  aiToolsOpen.value = false
  emit('action', action)
}

watch(() => props.gestureNoticeActive, showInitialHintsWithGestureNotice, { immediate: true })
watch(effectiveCollapsed, collapsed => {
  if (collapsed) {
    settingsOpen.value = false
    aiToolsOpen.value = false
  }
})
watch(() => props.landscapeMode, () => {
  settingsOpen.value = false
  aiToolsOpen.value = false
  cancelButtonHints()
})
watch(() => props.noticeActive, active => {
  if (!hintPending || hintCanOverlapNotice) return
  window.clearTimeout(hintTimer)
  hintsVisible.value = false
  if (!active) revealButtonHints()
})

onBeforeUnmount(cancelButtonHints)
</script>

<template>
  <aside
    class="tool-rail"
    :class="{
      'is-collapsed': effectiveCollapsed,
      'is-annotation-readonly': !annotationEditable,
      'are-hints-visible': hintsVisible,
      'are-hints-long-lived': longHintsVisible
    }"
    aria-label="模型工具"
  >
    <button
      class="tool-rail__collapse"
      type="button"
      :aria-label="effectiveCollapsed ? '展开工具栏' : '收起工具栏'"
      :aria-expanded="!effectiveCollapsed"
      aria-controls="viewer-tool-actions"
      @click="$emit('update:collapsed', !effectiveCollapsed)"
    >
      <ViewerIcon :name="effectiveCollapsed ? 'chevron-left' : 'chevron-right'" />
      <span class="tool-button__hint tool-button__hint--collapse" style="--hint-index:0" aria-hidden="true">{{ effectiveCollapsed ? '展开' : '收起' }}</span>
    </button>
    <div v-if="!effectiveCollapsed" id="viewer-tool-actions" class="tool-rail__items">
      <div class="tool-settings" :class="{ 'is-open': settingsOpen }">
        <button
          class="tool-button tool-settings__trigger"
          :class="{ 'is-active': settingsOpen }"
          type="button"
          data-tour="settings"
          aria-label="更多"
          :aria-expanded="settingsOpen"
          aria-controls="viewer-settings-actions"
          @click="toggleSettings"
        >
          <ViewerIcon name="more" />
          <span class="tool-button__hint tool-button__hint--upper" style="--hint-index:1" aria-hidden="true">{{ settingsOpen ? '收起' : '更多' }}</span>
        </button>
        <div id="viewer-settings-actions" class="tool-settings__menu" :aria-hidden="!settingsOpen">
          <button
            v-if="annotationEditable"
            class="tool-button"
            :class="{ 'is-active': annotationEditing }"
            type="button"
            data-tour="annotation"
            :aria-label="annotationEditing ? '退出标注编辑' : '标注'"
            :aria-pressed="annotationEditing"
            @click="handleSettingsAction('annotation')"
          >
            <ViewerIcon name="annotation" />
            <span class="tool-button__hint" aria-hidden="true">{{ annotationEditing ? '退出' : '标注' }}</span>
          </button>
          <button class="tool-button" type="button" data-tour="adjust" aria-label="参数设置" @click="handleSettingsAction('adjust')">
            <ViewerIcon name="sliders" />
            <span class="tool-button__hint" aria-hidden="true">参数</span>
          </button>
          <button
            v-if="cameraTrajectoryAvailable"
            class="tool-button"
            :class="{ 'is-active': cameraTrajectoryPlaying }"
            type="button"
            data-tour="camera-trajectory"
            :aria-label="cameraTrajectoryPlaying ? '停止播放拍摄轨迹' : '播放拍摄轨迹'"
            :aria-pressed="cameraTrajectoryPlaying"
            @click="handleSettingsAction('cameraTrajectory')"
          >
            <ViewerIcon :name="cameraTrajectoryPlaying ? 'stop' : 'play'" />
            <span class="tool-button__hint" aria-hidden="true">{{ cameraTrajectoryPlaying ? '停止' : '轨迹' }}</span>
          </button>
          <button
            v-if="videoRecordAvailable"
            class="tool-button"
            :class="{ 'is-active tool-button--recording': videoRecording }"
            type="button"
            data-tour="video-record"
            :aria-label="videoRecording ? '正在录制视频' : '录制视频'"
            :aria-pressed="videoRecording"
            :disabled="videoRecording"
            @click="handleSettingsAction('videoRecord')"
          >
            <ViewerIcon name="video-record" />
            <span class="tool-button__hint" aria-hidden="true">录制</span>
          </button>
          <button class="tool-button" type="button" data-tour="help" aria-label="查看操作教程" @click="handleSettingsAction('help')">
            <ViewerIcon name="help" />
            <span class="tool-button__hint" aria-hidden="true">教程</span>
          </button>
        </div>
      </div>
      <button
        class="tool-button"
        :class="{ 'tool-button--liked': liked }"
        type="button"
        data-tour="like"
        aria-label="点赞"
        :aria-pressed="liked"
        @click="$emit('action', 'like')"
      >
        <LikeBurstFeedback :active="liked"><ViewerIcon name="heart" :filled="liked" /></LikeBurstFeedback>
        <span class="tool-button__count" aria-label="喜欢数量">{{ likes }}</span>
        <span class="tool-button__hint tool-button__hint--upper" style="--hint-index:2" aria-hidden="true">{{ liked ? '取消' : '点赞' }}</span>
      </button>
      <button
        v-if="arAvailable"
        class="tool-button"
        type="button"
        data-tour="ar"
        aria-label="AR 投影"
        @click="$emit('action', 'ar')"
      >
        <ViewerIcon name="ar" />
        <span class="tool-button__hint tool-button__hint--upper" style="--hint-index:3" aria-hidden="true">投影</span>
      </button>
      <button
        class="tool-button"
        :class="{ 'is-active': landscapeMode }"
        type="button"
        data-tour="orientation"
        :aria-label="landscapeMode ? '切换到竖屏' : '切换到横屏'"
        :aria-pressed="landscapeMode"
        @click="$emit('action', 'orientation')"
      >
        <ViewerIcon :name="landscapeMode ? 'screen-portrait' : 'screen-landscape'" />
        <span class="tool-button__hint tool-button__hint--lower" style="--hint-index:4" aria-hidden="true">{{ landscapeMode ? '竖屏' : '横屏' }}</span>
      </button>
      <button
        class="tool-button"
        :class="{ 'is-active': cameraMode === 'fly' }"
        type="button"
        data-tour="mode"
        :aria-label="cameraMode === 'fly' ? '切换到轨道模式' : '切换到飞行模式'"
        :aria-pressed="cameraMode === 'fly'"
        @click="$emit('action', 'mode')"
      >
        <ViewerIcon :name="cameraMode === 'fly' ? 'mode-flight' : 'mode-orbit'" />
        <span class="tool-button__hint tool-button__hint--upper" style="--hint-index:5" aria-hidden="true">{{ cameraMode === 'fly' ? '轨道' : '飞行' }}</span>
      </button>
      <button class="tool-button" type="button" data-tour="reset" aria-label="重置视角" title="重置" @click="$emit('action', 'reset')">
        <ViewerIcon name="reset" />
        <span class="tool-button__hint tool-button__hint--upper" style="--hint-index:6" aria-hidden="true">重置</span>
      </button>
      <div class="tool-ai-menu" :class="{ 'is-open': aiToolsOpen }">
        <button
          class="tool-button tool-button--ai tool-button--ai-primary"
          :class="{
            'is-active': aiToolsOpen,
            'is-loading': aiLoading,
            'is-ready': aiStatus === 'success',
            'has-notice': aiNoticeVisible,
            'is-error': aiStatus === 'error'
          }"
          type="button"
          data-tour="ai"
          aria-label="展开 AI 工具，包含 AI 导游和 AI 视频"
          :aria-busy="aiLoading"
          :aria-expanded="aiToolsOpen"
          aria-controls="viewer-ai-actions"
          @click="toggleAiTools"
        >
          <span class="tool-button__ai-aura" aria-hidden="true"><i></i><i></i><i></i></span>
          <ViewerIcon name="ai" />
          <span class="tool-button__label" aria-hidden="true">AI</span>
          <span class="tool-button__ai-state" aria-hidden="true"></span>
          <span class="tool-button__hint tool-button__hint--lower" style="--hint-index:7" aria-hidden="true">AI工具</span>
        </button>
        <div id="viewer-ai-actions" class="tool-ai-menu__menu" :aria-hidden="!aiToolsOpen">
          <button
            class="tool-button tool-ai-menu__item tool-button--ai-child"
            :class="{
              'is-loading': aiGuideState === 'working',
              'is-ready': aiGuideState === 'ready',
              'is-error': aiGuideState === 'error'
            }"
            type="button"
            data-tour="ai-action"
            :aria-label="`${aiButtonLabel}，状态：${aiGuideStatusLabel}`"
            :aria-busy="aiLoading"
            @click="handleAiToolAction('ai')"
          >
            <span class="tool-button__ai-aura" aria-hidden="true"><i></i><i></i><i></i></span>
            <ViewerIcon name="ai" />
            <span class="tool-button__label" aria-hidden="true">AI导游</span>
            <span class="tool-button__status-dot" :class="`is-${aiGuideState}`" aria-hidden="true"></span>
          </button>
          <button
            class="tool-button tool-ai-menu__item"
            :class="{ 'is-active': imageVideoGenerating }"
            type="button"
            data-tour="image-video"
            :aria-label="imageVideoButtonLabel"
            :aria-pressed="imageVideoGenerating"
            @click="handleAiToolAction('imageVideo')"
          >
            <ViewerIcon name="video" />
            <span class="tool-button__label" aria-hidden="true">AI视频</span>
            <span class="tool-button__status-dot" :class="`is-${imageVideoState}`" aria-hidden="true"></span>
          </button>
        </div>
      </div>
      <button class="tool-button tool-button--share" type="button" data-tour="share" aria-label="分享" @click="$emit('action', 'share')">
        <ViewerIcon name="share" />
        <span class="tool-button__hint tool-button__hint--upper" style="--hint-index:8" aria-hidden="true">分享</span>
      </button>
    </div>
  </aside>
</template>
